import React from 'react';
import { Progress } from './ui/Progress';
import { Card, CardHeader, CardContent } from './ui/Card';
import { Badge } from './ui/Badge';

const STAGES = [
  { id: 'cleaning', name: 'Очистка', icon: 'bi-trash' },
  { id: 'loading', name: 'Загрузка', icon: 'bi-download' },
  { id: 'preparing', name: 'Demucs', icon: 'bi-gear' },
  { id: 'speech_recognition', name: 'Whisper', icon: 'bi-mic' },
  { id: 'genre_detection', name: 'MERT', icon: 'bi-vinyl' },
  { id: 'analyzing', name: 'GPT-OSS', icon: 'bi-cpu' },
];

// Fallback bounds when the backend doesn't return stage_bounds yet.
const DEFAULT_BOUNDS = { cleaning: [0, 5], loading: [5, 45], preparing: [45, 60], speech_recognition: [60, 72], genre_detection: [72, 85], analyzing: [85, 100] };

const getStatus = (id, cur) => {
  const i = STAGES.findIndex((s) => s.id === id), ci = STAGES.findIndex((s) => s.id === cur);
  return i < ci ? 'completed' : i === ci ? 'active' : 'pending';
};

const getPct = (id, cur, p, bounds) => {
  const s = getStatus(id, cur);
  if (s === 'completed') return 100;
  if (s === 'pending') return 0;
  const ranges = bounds || DEFAULT_BOUNDS;
  const [min, max] = ranges[id] || [0, 100];
  return Math.max(0, Math.min(100, ((p - min) / (max - min)) * 100));
};

/* Состояние этапа передаётся и цветом, и иконкой:
   завершён — галочка, текущий — иконка этапа, ожидает — приглушённая иконка. */
const STAGE_STYLES = {
  completed: {
    circle: 'border-success/40 bg-success-soft text-success',
    label: 'text-success',
    tone: 'success',
  },
  active: {
    circle: 'border-primary/40 bg-primary-soft text-primary',
    label: 'text-text-strong',
    tone: 'primary',
  },
  pending: {
    circle: 'border-border bg-muted text-text-muted',
    label: 'text-text-muted',
    tone: 'primary',
  },
};

const STAGE_STATE_LABEL = { completed: 'завершён', active: 'выполняется', pending: 'ожидает' };

const ProcessingProgress = ({ status }) => {
  if (!status || status.status === 'idle') return null;

  if (status.status === 'error') {
    return (
      <div
        className="mb-3xl flex items-start gap-md rounded-md border border-destructive/30 bg-destructive-soft p-lg text-body-sm text-destructive animate-fade-in"
        role="alert"
      >
        <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
        <span>{status.message}</span>
      </div>
    );
  }

  const isCompleted = status.status === 'completed';

  return (
    <Card className="mb-3xl animate-fade-in">
      <CardHeader
        title="Прогресс обработки"
        icon="bi-activity"
        actions={
          <Badge
            variant={isCompleted ? 'success' : 'primary'}
            icon={isCompleted ? 'bi-check-circle' : 'bi-arrow-repeat'}
          >
            {isCompleted ? 'Готово' : 'В работе'}
          </Badge>
        }
      />
      <CardContent>
        <div className="mb-sm flex flex-wrap items-center justify-between gap-md">
          <span className="tabular text-body-sm text-text-strong">{status.progress}%</span>
          {status.processed_count != null && (
            <span className="text-body-xs text-text-muted">
              Обработано: <span className="tabular text-text">{status.processed_count}/{status.total_count}</span>
            </span>
          )}
        </div>

        <Progress value={status.progress} tone={isCompleted ? 'success' : 'primary'} className="mb-xl" />

        <ol className="flex flex-col gap-md sm:flex-row sm:gap-lg">
          {STAGES.map((stage) => {
            const s = getStatus(stage.id, status.stage);
            const pct = getPct(stage.id, status.stage, status.progress, status.stage_bounds);
            const style = STAGE_STYLES[s];

            return (
              <li key={stage.id} className="flex min-w-0 flex-1 items-center gap-md sm:flex-col sm:gap-sm">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-pill border transition-colors duration-300 ${style.circle}`}
                  title={`${stage.name}: ${STAGE_STATE_LABEL[s]}`}
                >
                  <i
                    className={`bi ${s === 'completed' ? 'bi-check-lg' : stage.icon}`}
                    aria-hidden="true"
                  />
                </span>
                <div className="min-w-0 flex-1 sm:w-full sm:text-center">
                  <p className={`font-mono text-eyebrow uppercase tracking-eyebrow transition-colors duration-300 ${style.label}`}>
                    {stage.name}
                    <span className="sr-only"> — {STAGE_STATE_LABEL[s]}</span>
                  </p>
                  <Progress value={pct} tone={style.tone} className="mt-sm h-1" />
                </div>
              </li>
            );
          })}
        </ol>

        <div className="mt-lg text-center">
          <p className="text-body-sm text-text-muted">{status.message}</p>
          {status.status === 'processing' && ['cleaning', 'loading', 'preparing'].includes(status.stage) && (
            <p className="mt-xs text-body-xs text-warning">
              <i className="bi bi-info-circle mr-1" aria-hidden="true"></i>
              {status.stage === 'cleaning' ? 'Очистка предыдущих данных' : 'Предыдущие данные будут заменены'}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProcessingProgress;
